import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

// Phone mockup styled components
const PhoneContainer = styled.div`
  max-width: 375px;
  margin: 0 auto;
  position: relative;
  display: flex;
  justify-content: center;
`;

const PhoneDevice = styled.div`
  position: relative;
  width: 100%;
  max-width: 375px;
  min-height: 300px;
  height: auto;
  background: #1a1a1a;
  border-radius: 40px 40px 20px 20px;
  padding: 10px;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1), 0 20px 50px rgba(0, 0, 0, 0.3),
    0 10px 20px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    transform: scale(0.9);
    transform-origin: top center;
  }
`;

const PhoneBezel = styled.div`
  position: relative;
  width: 100%;
  min-height: 280px;
  height: auto;
  background: #000;
  border-radius: 32px 32px 12px 12px;
  overflow: hidden;
`;

const PhoneScreen = styled.div`
  position: relative;
  min-height: 280px;
  height: auto;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'linear-gradient(to bottom, #1a1a2e, #0f0f1e)'
      : 'linear-gradient(to bottom, #f0f0f0, #ffffff)'};
  display: flex;
  flex-direction: column;
`;

const StatusBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 24px 8px;
  color: ${(props) =>
    props.theme.name === 'dark' ? '#ffffff' : props.theme.colors.blueBlack};
  font-size: 14px;
  font-weight: 600;
`;

const StatusLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StatusRight = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const DynamicIsland = styled.div`
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  width: 120px;
  height: 32px;
  background: #000;
  border-radius: 20px;
  z-index: 10;
`;

const MessagesHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)'};
`;

const ContactInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ContactAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #fa4616;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 14px;
`;

const ContactName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${(props) =>
    props.theme.name === 'dark' ? '#ffffff' : props.theme.colors.blueBlack};
`;

const MessagesContainer = styled.div`
  flex: 1;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-height: 150px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'url("data:image/svg+xml,%3Csvg width="40" height="40" xmlns="http://www.w3.org/2000/svg"%3E%3Cdefs%3E%3Cpattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse"%3E%3Cpath d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.02)" stroke-width="1"/%3E%3C/pattern%3E%3C/defs%3E%3Crect width="100%25" height="100%25" fill="url(%23grid)" /%3E%3C/svg%3E")'
      : 'url("data:image/svg+xml,%3Csvg width="40" height="40" xmlns="http://www.w3.org/2000/svg"%3E%3Cdefs%3E%3Cpattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse"%3E%3Cpath d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(0,0,0,0.02)" stroke-width="1"/%3E%3C/pattern%3E%3C/defs%3E%3Crect width="100%25" height="100%25" fill="url(%23grid)" /%3E%3C/svg%3E")'};
`;

const MessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const MessageBubble = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? '#E5E5EA' // Light gray for both themes - matches real SMS
      : '#E5E5EA'};
  color: ${(props) =>
    props.theme.colors
      .blueBlack}; // Blue-black text for better contrast and emoji visibility
  padding: 12px 16px;
  border-radius: 20px 20px 20px 4px;
  max-width: 85%;
  font-size: 15px;
  line-height: 1.4;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  white-space: pre-wrap; // Preserve line breaks
  word-break: break-word; // Wrap long URLs
  overflow-wrap: break-word; // Additional support for URL wrapping

  a {
    color: #007aff; // iOS blue link color
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const MessageTime = styled.div`
  font-size: 11px;
  color: ${(props) => (props.theme.name === 'dark' ? '#9CA3AF' : '#6B7280')};
  margin-left: 8px;
`;

const NotificationContainer = styled.div`
  position: relative;
  padding: 20px 12px;
  z-index: 20;
`;

const PushNotification = styled.div`
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  padding: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3), 0 2px 10px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(0, 0, 0, 0.05);
  word-break: break-word;
`;

const PushContent = styled.div`
  display: flex;
  gap: 12px;
`;

const PushIcon = styled.div`
  width: 40px;
  height: 40px;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    border-radius: 10px;
  }
`;

const PushText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0; /* Allow flex item to shrink below content size */
`;

const PushTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
  word-break: break-word;
  overflow-wrap: break-word;
`;

const PushBody = styled.div`
  font-size: 14px;
  color: #6b7280;
  line-height: 1.4;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: pre-wrap; /* Preserve line breaks like SMS */
  max-height: none; /* Allow unlimited height */
`;

const PushMeta = styled.div`
  font-size: 12px;
  color: #9ca3af;
`;

const PhoneMockup = ({
  channel,
  message,
  pushTitle,
  theme = { name: 'light' },
}) => {
  // Function to detect and linkify URLs in text
  const linkifyText = (text) => {
    if (!text) return text;

    // Improved regex to match URLs including those with special characters
    // This will match until it hits whitespace or end of string
    const urlRegex = /(https?:\/\/[^\s\n]+)/g;
    const parts = text.split(urlRegex);

    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            style={{ wordBreak: 'break-all' }}
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  const getPlatformLogo = () => {
    // Use platform app icons - same as original SendCampaign
    if (process.env.REACT_APP_PLATFORM === 'tickethoss') {
      return '/images/platform-logos/tickethoss-logo-5.png';
    } else if (process.env.REACT_APP_PLATFORM === 'kartpass') {
      return '/images/platform-logos/kartpass-app-icon.png';
    } else {
      // Default to PitPay
      return '/images/platform-logos/pitpay-app-icon.png';
    }
  };

  const getPlatformName = () => {
    return process.env.REACT_APP_PLATFORM === 'pitpay'
      ? 'PitPay'
      : 'Ticket Hoss';
  };

  const currentTime = new Date().toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return (
    <PhoneContainer>
      <PhoneDevice>
        <PhoneBezel>
          <DynamicIsland />
          <PhoneScreen theme={theme}>
            <StatusBar theme={theme}>
              <StatusLeft>{currentTime}</StatusLeft>
              <StatusRight>
                <svg
                  width="17"
                  height="12"
                  viewBox="0 0 17 12"
                  fill="currentColor"
                >
                  <path
                    d="M1 4C3.5 4 5.5 2 8 2C10.5 2 12.5 4 15 4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    fill="none"
                  />
                  <path
                    d="M3 7C4.5 7 6 6 8 6C10 6 11.5 7 13 7"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    fill="none"
                  />
                  <path
                    d="M5 10C6 10 7 9.5 8 9.5C9 9.5 10 10 11 10"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    fill="none"
                  />
                </svg>
                <svg
                  width="17"
                  height="12"
                  viewBox="0 0 17 12"
                  fill="currentColor"
                >
                  <rect x="1" y="4" width="3" height="8" rx="0.5" />
                  <rect x="5" y="2" width="3" height="10" rx="0.5" />
                  <rect x="9" y="0" width="3" height="12" rx="0.5" />
                  <rect x="13" y="2" width="3" height="10" rx="0.5" />
                </svg>
                <svg
                  width="25"
                  height="12"
                  viewBox="0 0 25 12"
                  fill="currentColor"
                >
                  <rect
                    x="1"
                    y="2"
                    width="21"
                    height="8"
                    rx="2"
                    stroke="currentColor"
                    fill="none"
                    strokeWidth="1"
                  />
                  <rect
                    x="23"
                    y="5"
                    width="1"
                    height="2"
                    rx="0.5"
                    fill="currentColor"
                  />
                  <rect
                    x="3"
                    y="4"
                    width="17"
                    height="4"
                    rx="1"
                    fill="currentColor"
                  />
                </svg>
              </StatusRight>
            </StatusBar>

            {channel === 'SMS' ? (
              <>
                <MessagesHeader theme={theme}>
                  <ContactInfo>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path
                        d="M15 19l-7-7 7-7"
                        stroke="#FA4616"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <ContactAvatar>TH</ContactAvatar>
                    <ContactName theme={theme}>{getPlatformName()}</ContactName>
                  </ContactInfo>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="#FA4616"
                  >
                    <circle cx="12" cy="12" r="2" />
                    <circle cx="12" cy="5" r="2" />
                    <circle cx="12" cy="19" r="2" />
                  </svg>
                </MessagesHeader>

                <MessagesContainer theme={theme}>
                  <MessageWrapper>
                    <MessageBubble theme={theme}>
                      {linkifyText(
                        message ||
                          "Hey! Don't miss out on this special offer at the track this weekend! 🏁"
                      )}
                      {/* No longer showing attachment images - they are now links in the message text */}
                    </MessageBubble>
                    <MessageTime theme={theme}>Delivered</MessageTime>
                  </MessageWrapper>
                </MessagesContainer>
              </>
            ) : (
              <NotificationContainer>
                <PushNotification theme={theme}>
                  <PushContent>
                    <PushIcon>
                      <img src={getPlatformLogo()} alt={getPlatformName()} />
                    </PushIcon>
                    <PushText>
                      <PushTitle theme={theme}>
                        {pushTitle || `${getPlatformName()} Event Update`}
                      </PushTitle>
                      <PushBody theme={theme}>
                        {message ||
                          'Get ready for an exciting race day! Gates open at 5 PM.'}
                      </PushBody>
                      <PushMeta theme={theme}>
                        now • {getPlatformName()}
                      </PushMeta>
                    </PushText>
                  </PushContent>
                </PushNotification>
              </NotificationContainer>
            )}
          </PhoneScreen>
        </PhoneBezel>
      </PhoneDevice>
    </PhoneContainer>
  );
};

PhoneMockup.propTypes = {
  channel: PropTypes.oneOf(['SMS', 'Push']).isRequired,
  message: PropTypes.string.isRequired,
  pushTitle: PropTypes.string,
  attachments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      size: PropTypes.number.isRequired,
      sizeFormatted: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    })
  ),
  theme: PropTypes.object,
};

export default PhoneMockup;
